<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import type { MenuItem } from 'primevue/menuitem';
import SplitButton from 'primevue/splitbutton';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import Dialog from 'primevue/dialog';
import Menu from 'primevue/menu';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import InputText from 'primevue/inputtext';
import Listbox from 'primevue/listbox';
import {
  useConfirmUnsavedChanges,
  useDebounce,
  useIndustryCodes,
  useOpportunities,
  useStringFunctions,
} from '@apparatix/composables';
import RevenueProjections from '@apparatix/leads/RevenueProjections.vue';
import type { OpportunityType } from '@apparatix/types/Leads';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import Modal from '@apparatix/Components/Modal.vue';
import Avatar from 'primevue/avatar';
import CreateCampaignWizard from './CreateCampaignWizard.vue';
import MapMarkerIcon from '@primeicons/vue/map-marker';
import CogIcon from '@primeicons/vue/cog';
import UserPlusIcon from '@primeicons/vue/user-plus';
import DollarIcon from '@primeicons/vue/dollar';
import SearchIcon from '@primeicons/vue/search';
import ChartLineIcon from '@primeicons/vue/chart-line';
import FileExportIcon from '@primeicons/vue/file-export';
import PencilIcon from '@primeicons/vue/pencil';
import EllipsisHIcon from '@primeicons/vue/ellipsis-h';

interface SwimlanesHeaderProps {
  totalValue: number;
  type: OpportunityType;
  existingGlobalSearchQuery?: string;
}

const props = defineProps<SwimlanesHeaderProps>();
const { formatAsCurrency } = useStringFunctions();
const store = useOpportunityStore();
const { saveLanesVisible } = useOpportunities();

const { industryCodeRequired } = useIndustryCodes();

// @TODO: this might also have a better strategy
const viewFormat = computed(() => store.viewFormat[props.type]);

const emit = defineEmits<{
  (e: 'update:global-search-value', value: string): void;
  (e: 'export:data-table');
}>();

const searchPlaceholder = computed<string>(() => props.type === 'Opportunity' ? 'Search Opportunities' : 'Search Leads');

const wizardTitle = computed(() => {
  if (store.createOpportunityContext === 'convert-lead') return 'Convert Lead to Opportunity';
  if (store.createOpportunityContext === 'create-lead') return 'Create Lead';
  return 'Create Opportunity';
});

const globalSearchQuery = ref(props.existingGlobalSearchQuery ?? '');
const moreMenuRef = ref();
const projectionsVisible = ref(false);
const showConfigureLanesPopup = ref(false);
const showCreateLeadWizard = ref(false);
const leadWizardHasUnsavedChanges = ref(false);
const suppressWizardWarning = ref(false);

const debouncedSearchQuery = useDebounce(globalSearchQuery, 300);

const { component: UnsavedChanges, onClick: handleCloseWizard } = useConfirmUnsavedChanges(() => leadWizardHasUnsavedChanges.value, () => {
  showCreateLeadWizard.value = false;
  suppressWizardWarning.value = true;
  if (store.showOpportunityCreationModal) {
    closeOpportunityCreationModal();
  }
}, 'dialog');

watch(debouncedSearchQuery, newQuery => emit('update:global-search-value', newQuery));

const addButtonItems = ref<MenuItem[]>([
  {
    label: 'Map search',
    icon: MapMarkerIcon,
    command: () => {
      // mapl.html is intentional and not a typo
      window.open('/apx/TAG/accounts/mapl.html', '_blank');

    },
  },
]);

const moreMenuOptions = ref<MenuItem[]>([
  {
    label: 'Options',
    items: [
      {
        label: 'Configure visible lanes',
        icon: CogIcon,
        command: () => showConfigureLanesPopup.value = true,
      },
    ],
  },
]);

const onAddButtonClick = () => {
  suppressWizardWarning.value = false;
  if (props.type === 'Lead') {
    store.createOpportunityContext = 'create-lead';
    store.showOpportunityCreationModal = true;
    return;
  }

  store.createOpportunityContext = 'create-opportunity';
  store.showOpportunityCreationModal = true;
};
const onProjectionClick = () => projectionsVisible.value = true;
const onProjectionClose = () => projectionsVisible.value = false;

const closeOpportunityCreationModal = () => {
  store.showOpportunityCreationModal = false;
  store.createOpportunityContext = undefined;
};

const onMoreButtonClick = (e: MouseEvent) => moreMenuRef.value.toggle(e);

const onModalClose = () => {
  if (suppressWizardWarning.value) {
    closeOpportunityCreationModal();
  } else {
    handleCloseWizard();
  }
};

const onCampaignWizardClose = async () => {
  suppressWizardWarning.value = true;
  await nextTick();

  onModalClose();
};
</script>
<template>
  <div class="flex flex-wrap sm:flex-nowrap gap-4 sm:items-center sm:justify-between">
    <div v-if="props.type === 'Lead'" class="flex items-center gap-2">
      <Avatar
        size="normal"
        shape="circle"
        style="background-color: var(--p-amber-100); color: var(--p-amber-500);"
      >
        <template #icon>
          <UserPlusIcon />
        </template>
      </Avatar>
      <h1 class="text-lg">
        Leads
      </h1>
      <IconField icon-position="left">
        <InputIcon>
          <SearchIcon />
        </InputIcon>
        <InputText
          v-model="globalSearchQuery"
          :placeholder="searchPlaceholder"
          class="max-w-[100px] lg:max-w-[195px]"
        />
      </IconField>
    </div>
    <div v-else class="flex items-center gap-2">
      <Avatar
        size="normal"
        shape="circle"
        style="background-color: var(--p-lime-100); color: var(--p-lime-500);"
      >
        <template #icon>
          <DollarIcon />
        </template>
      </Avatar>
      <h1 class="text-lg">
        Opportunities
      </h1>
      <IconField icon-position="left">
        <InputIcon>
          <SearchIcon />
        </InputIcon>
        <InputText
          v-model="globalSearchQuery"
          :placeholder="searchPlaceholder"
          class="max-w-[100px] lg:max-w-[195px]"
        />
      </IconField>
    </div>
    <div />
    <div class="flex flex-wrap sm:flex-row gap-4 sm:gap-4">
      <!--  @TODO: Below is temporary condition until we are updating Projected Total with Table view filtering  -->
      <!--  @TODO: We probably need to hit a GET request with a list of filtered ids behind a debounce  -->
      <div v-if="viewFormat === 'swimlanes'" class="flex-none hidden lg:flex lg:gap-3 items-center justify-center">
        <span class="font-normal text-sm text-[var(--swimlane-secondary-text)]">{{ formatAsCurrency(props.totalValue, false) }} Projected Value</span>
      </div>
      <Button
        v-if="props.type === 'Opportunity'"
        aria-label="Projections"
        severity="secondary"
        outlined
        @click="onProjectionClick"
      >
        <ChartLineIcon />
        <span class="hidden xl:inline font-bold">Projections</span>
      </Button>
      <Button
        v-if="store.viewFormat[props.type] !== 'swimlanes'"
        v-tooltip.left="'Export'"
        outlined
        severity="secondary"
        class="flex items-center"
        @click="emit('export:data-table')"
      >
        <FileExportIcon />
        <span class="font-bold">Export</span>
      </Button>
      <SplitButton
        v-if="props.type === 'Lead'"
        :model="addButtonItems"
        :disabled="store.currentState[props.type] !== 'Active'"
        @click="onAddButtonClick"
      >
        <PencilIcon />
        <span class="hidden xl:inline font-bold">Create {{ props.type }}</span>
      </SplitButton>
      <Button
        v-else
        class="font-bold"
        :model="addButtonItems"
        :disabled="store.currentState[props.type] !== 'Active'"
        @click="onAddButtonClick"
      >
        <PencilIcon />
        <span class="hidden xl:inline font-bold">Create {{ props.type }}</span>
      </Button>
      <!--BELOW IS INTENTIONALLY LEFT AS FALSE UNTIL MORE USE CASES FOR IT-->
      <div v-if="false" class="flex-none">
        <Button
          icon-only
          aria-label="More options"
          severity="secondary"
          outlined
          @click="onMoreButtonClick"
        >
          <EllipsisHIcon />
        </Button>
      </div>
    </div>
  </div>
  <Menu ref="moreMenuRef" :model="moreMenuOptions" :popup="true" />
  <Dialog
    v-model:visible="showConfigureLanesPopup"
    header="Configure visible lanes"
    position="top"
    modal
    @after-hide="saveLanesVisible(props.type, store.selectedVisibleLanes[props.type])"
  >
    <Listbox
      v-model="store.selectedVisibleLanes[props.type]"
      :options="store.swimlanes[props.type]"
      option-label="status"
      multiple
    />
  </Dialog>
  <Modal
    :title="wizardTitle"
    :visible="store.showOpportunityCreationModal"
    disable-footer
    @close="onModalClose"
  >
    <template #body>
      <CreateCampaignWizard
        :industry-code-required="industryCodeRequired"
        :type="store.createOpportunityContext === 'convert-lead' ? 'Opportunity' : props.type"
        :edit-lead="store.selectedOpportunity['Lead']"
        @close="onCampaignWizardClose"
        @update:has-unsaved-changes="hasChanges => leadWizardHasUnsavedChanges = hasChanges"
      />
    </template>
  </Modal>
  <ConfirmDialog group="closeConfirm" />
  <Modal
    title="Opportunity Projections"
    :visible="projectionsVisible"
    :disable-accept="true"
    :disable-cancel="true"
    :disable-footer="true"
    @close="onProjectionClose"
  >
    <template #body>
      <RevenueProjections :type="props.type" />
    </template>
  </Modal>
  <component :is="UnsavedChanges" />
</template>

<style scoped>
.p-button-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}
</style>
